import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createSvgEl, createZoomViewport } from '@learnforge/demo-render';
import { createToggleRow } from '@learnforge/demo-kit';
import { createAxes } from '../render/axes';
import { createDragHandle } from '../render/drag-handle';
import { createTriangle, type Point } from '../render/triangle';
import { solveTriangle, triangleArea } from '../trig/triangle';
import { radToDeg } from '../trig/angles';
import { createActionBar } from '../action-bar';
import type { TrigContext } from '../types';
import type { TrigKey } from '../i18n';

const BASE_VIEW = { x: -7, y: -5, width: 14, height: 10 };

// ---------------------------------------------------------------------------
// Re-exports (preserve existing public surface)
// ---------------------------------------------------------------------------

export { solveTriangle, triangleArea } from '../trig/triangle';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function f(v: number): string {
  return v.toFixed(2);
}

function n(v: number): string {
  return `<span class="num">${f(v)}</span>`;
}

function makeSection(titleText: string): { section: HTMLDivElement; body: HTMLDivElement } {
  const section = document.createElement('div');
  section.className = 'tg-ts-section';
  const title = document.createElement('div');
  title.className = 'tg-ts-section-title';
  title.textContent = titleText;
  section.appendChild(title);
  const body = document.createElement('div');
  section.appendChild(body);
  return { section, body };
}

function row(html: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'tg-ts-row';
  el.innerHTML = html;
  return el;
}

// ---------------------------------------------------------------------------
// State — model only (no DOM refs)
// ---------------------------------------------------------------------------

interface State {
  v1: Point;
  v2: Point;
  v3: Point;
  showGhost: boolean;
  snapIntegers: boolean;
}

// ---------------------------------------------------------------------------
// Spec factory
// ---------------------------------------------------------------------------

function buildSpec(): CustomExperimentSpec<State, TrigKey, TrigContext> {
  return {
    labelKey: 'experiment.triangle-solver.label',
    demoId: 'trigonometry',
    advancedKey: 'triangle-solver',

    initialState(): State {
      return {
        v1: { x: -2, y: -1.5 },
        v2: { x: 2, y: -1.5 },
        v3: { x: 0, y: 2.0 },
        showGhost: true,
        snapIntegers: false,
      };
    },

    createRender(ctx, host, controls) {
      // i18n subscription tracking
      const i18nUnsubs: Array<() => void> = [];
      function trackSubscribe(callback: () => void): void {
        const off = ctx.i18n.subscribe(callback);
        i18nUnsubs.push(off);
      }

      // ---- SVG stage ----
      const svg = createSvgEl('svg', {
        viewBox: `${BASE_VIEW.x} ${BASE_VIEW.y} ${BASE_VIEW.width} ${BASE_VIEW.height}`,
        preserveAspectRatio: 'xMidYMid meet',
        class: 'tg-svg',
        'font-size': '0.25',
      });
      host.appendChild(svg);

      // Y-flip so math coords point up
      const world = createSvgEl('g', { transform: 'scale(1 -1)' });
      svg.appendChild(world);

      // Axes (no grid per spec)
      const axes = createAxes({
        xDomain: [-7, 7],
        yDomain: [-5, 5],
        showGrid: false,
        i18n: ctx.i18n,
      });
      world.appendChild(axes.group);

      // Ghost group (below primary triangle)
      const ghostGroup = createSvgEl('g', { class: 'tg-ghost-group' });
      world.appendChild(ghostGroup);

      // Primary triangle
      const triangle = createTriangle(world, {
        vertices: [
          { x: -2, y: -1.5 },
          { x: 2, y: -1.5 },
          { x: 0, y: 2.0 },
        ],
        showSideLabels: ['a', 'b', 'c'],
        i18n: ctx.i18n,
      });

      // ---- Drag handles ----
      const handleGroup = createSvgEl('g');
      world.appendChild(handleGroup);

      // Closure variable: always reflects the last update(state) call.
      // onMove/snap callbacks read this so they see live state without
      // mutating closure scalars.
      let current: State = {
        v1: { x: -2, y: -1.5 },
        v2: { x: 2, y: -1.5 },
        v3: { x: 0, y: 2.0 },
        showGhost: true,
        snapIntegers: false,
      };

      function snapFn(x: number, y: number): { x: number; y: number } {
        if (!current.snapIntegers) return { x, y };
        return { x: Math.round(x), y: Math.round(y) };
      }

      const handles = [
        createDragHandle(handleGroup, {
          cx: -2,
          cy: -1.5,
          r: 0.25,
          snap: snapFn,
          onMove: (p) => {
            controls.setState((s) => ({ ...s, v1: p }));
          },
        }),
        createDragHandle(handleGroup, {
          cx: 2,
          cy: -1.5,
          r: 0.25,
          snap: snapFn,
          onMove: (p) => {
            controls.setState((s) => ({ ...s, v2: p }));
          },
        }),
        createDragHandle(handleGroup, {
          cx: 0,
          cy: 2.0,
          r: 0.25,
          snap: snapFn,
          onMove: (p) => {
            controls.setState((s) => ({ ...s, v3: p }));
          },
        }),
      ];

      const viewport = createZoomViewport(svg, {
        base: BASE_VIEW,
        yUp: true,
        fontSize: 0.25,
        onChange: (view) => {
          axes.update({ xDomain: view.xDomain, yDomain: view.yDomain, chromeScale: 1 / view.zoom });
          for (const h of handles) h.setScale(1 / view.zoom);
        },
      });

      // ---- Panel readout elements ----
      // Rows that redrawReadouts() will update via innerHTML.
      let sidesBody: HTMLDivElement;
      let anglesBody: HTMLDivElement;
      let sineRuleBody: HTMLDivElement;
      let cosineRuleBody: HTMLDivElement;
      let areaBody: HTMLDivElement;

      // Build panel DOM ONCE.
      {
        // Sides section
        const sidesSection = makeSection(ctx.i18n.t('ts.section.sides'));
        trackSubscribe(() => {
          const title = sidesSection.section.querySelector('.tg-ts-section-title');
          if (title) title.textContent = ctx.i18n.t('ts.section.sides');
        });
        sidesBody = sidesSection.body;
        ctx.panel.appendChild(sidesSection.section);

        // Angles section
        const anglesSection = makeSection(ctx.i18n.t('ts.section.angles'));
        trackSubscribe(() => {
          const title = anglesSection.section.querySelector('.tg-ts-section-title');
          if (title) title.textContent = ctx.i18n.t('ts.section.angles');
        });
        anglesBody = anglesSection.body;
        ctx.panel.appendChild(anglesSection.section);

        // Sine rule section
        const sineSection = makeSection(ctx.i18n.t('ts.section.sineRule'));
        trackSubscribe(() => {
          const title = sineSection.section.querySelector('.tg-ts-section-title');
          if (title) title.textContent = ctx.i18n.t('ts.section.sineRule');
        });
        sineRuleBody = sineSection.body;
        ctx.panel.appendChild(sineSection.section);

        // Cosine rule section
        const cosineSection = makeSection(ctx.i18n.t('ts.section.cosineRule'));
        trackSubscribe(() => {
          const title = cosineSection.section.querySelector('.tg-ts-section-title');
          if (title) title.textContent = ctx.i18n.t('ts.section.cosineRule');
        });
        cosineRuleBody = cosineSection.body;
        ctx.panel.appendChild(cosineSection.section);

        // Area section
        const areaSection = makeSection(ctx.i18n.t('ts.section.area'));
        trackSubscribe(() => {
          const title = areaSection.section.querySelector('.tg-ts-section-title');
          if (title) title.textContent = ctx.i18n.t('ts.section.area');
        });
        areaBody = areaSection.body;
        ctx.panel.appendChild(areaSection.section);

        // Ghost toggle (static — no section wrapper needed)
        const ghostToggleRow = createToggleRow({
          labelKey: 'ts.ambiguous.toggle',
          checked: true,
          onChange: (v) => {
            controls.setState((s) => ({ ...s, showGhost: v }));
          },
          i18n: ctx.i18n,
        });
        const ghostSection = document.createElement('div');
        ghostSection.className = 'tg-ts-section';
        ghostSection.appendChild(ghostToggleRow.element);
        ctx.panel.appendChild(ghostSection);
      }

      // ---- Action bar ----
      const actionBar = createActionBar(ctx.actionBar, {
        buttons: [
          {
            id: 'reset',
            labelKey: 'ts.reset',
            onClick: () => {
              controls.setState((s) => ({
                ...s,
                v1: { x: -2, y: -1.5 },
                v2: { x: 2, y: -1.5 },
                v3: { x: 0, y: 2.0 },
              }));
            },
          },
          {
            id: 'snap',
            labelKey: 'ts.snap.integers',
            onClick: () => {
              controls.setState((s) => {
                const next = !s.snapIntegers;
                actionBar.setActive('snap', next);
                return { ...s, snapIntegers: next };
              });
            },
          },
          { id: 'view', labelKey: 'view.reset', onClick: () => viewport.reset() },
        ],
        i18n: ctx.i18n,
      });
      actionBar.setActive('snap', false);

      // ---------------------------------------------------------------------------
      // Geometry helpers
      // ---------------------------------------------------------------------------

      function currentGeometry(state: State): {
        a: number;
        b: number;
        c: number;
        A: number;
        B: number;
        C: number;
      } {
        const { v1, v2, v3 } = state;
        // Side lengths: a = BC (opposite v1), b = AC (opposite v2), c = AB (opposite v3)
        const a = Math.hypot(v3.x - v2.x, v3.y - v2.y);
        const b = Math.hypot(v3.x - v1.x, v3.y - v1.y);
        const c = Math.hypot(v2.x - v1.x, v2.y - v1.y);
        // Angles via law of cosines, clamped for safety
        const cosA = (b * b + c * c - a * a) / (2 * b * c);
        const cosB = (a * a + c * c - b * b) / (2 * a * c);
        const A = Math.acos(Math.min(1, Math.max(-1, cosA)));
        const B = Math.acos(Math.min(1, Math.max(-1, cosB)));
        const C = Math.PI - A - B;
        return { a, b, c, A, B, C };
      }

      // ---------------------------------------------------------------------------
      // Readouts
      // ---------------------------------------------------------------------------

      function redrawReadouts(state: State): void {
        const g = currentGeometry(state);
        const { a, b, c, A, B, C } = g;

        // ── Sides ───────────────────────────────────────────────────────────────
        sidesBody.replaceChildren(row(`a ≈ ${n(a)}`), row(`b ≈ ${n(b)}`), row(`c ≈ ${n(c)}`));

        // ── Angles ──────────────────────────────────────────────────────────────
        anglesBody.replaceChildren(
          row(`A ≈ ${n(radToDeg(A))}°`),
          row(`B ≈ ${n(radToDeg(B))}°`),
          row(`C ≈ ${n(radToDeg(C))}°`),
        );

        // ── Sine rule  a/sin A = b/sin B = c/sin C ──────────────────────────────
        const sinA = Math.sin(A);
        const sinB = Math.sin(B);
        const sinC = Math.sin(C);
        const ratioA = sinA > 0 ? a / sinA : 0;
        const ratioB = sinB > 0 ? b / sinB : 0;
        const ratioC = sinC > 0 ? c / sinC : 0;
        sineRuleBody.replaceChildren(
          row(`a / sin A  ≈  ${n(a)} / ${n(sinA)}  ≈  ${n(ratioA)}`),
          row(`b / sin B  ≈  ${n(b)} / ${n(sinB)}  ≈  ${n(ratioB)}`),
          row(`c / sin C  ≈  ${n(c)} / ${n(sinC)}  ≈  ${n(ratioC)}`),
        );

        // ── Cosine rule  (one verification per angle) ───────────────────────────
        // a² ≈ b² + c² − 2bc cos A
        const lhsA = a * a;
        const rhsA = b * b + c * c - 2 * b * c * Math.cos(A);
        // b² ≈ a² + c² − 2ac cos B
        const lhsB = b * b;
        const rhsB = a * a + c * c - 2 * a * c * Math.cos(B);
        // c² ≈ a² + b² − 2ab cos C
        const lhsC = c * c;
        const rhsC = a * a + b * b - 2 * a * b * Math.cos(C);
        cosineRuleBody.replaceChildren(
          row(`a² ≈ b² + c² − 2bc cos A`),
          row(`${n(lhsA)} ≈ ${n(rhsA)}`),
          row(``),
          row(`b² ≈ a² + c² − 2ac cos B`),
          row(`${n(lhsB)} ≈ ${n(rhsB)}`),
          row(``),
          row(`c² ≈ a² + b² − 2ab cos C`),
          row(`${n(lhsC)} ≈ ${n(rhsC)}`),
        );

        // ── Area  = ½ · a · b · sin C ───────────────────────────────────────────
        const area = triangleArea({ a, b, c, A, B, C });
        areaBody.replaceChildren(
          row(`Area = ½ · a · b · sin C`),
          row(`     ≈ ½ · ${n(a)} · ${n(b)} · ${n(sinC)}`),
          row(`     ≈ ${n(area)}`),
        );
      }

      // ---------------------------------------------------------------------------
      // Ghost triangle (SSA ambiguous case)
      // ---------------------------------------------------------------------------

      function maybeShowGhost(state: State): void {
        // Clear previous ghost
        while (ghostGroup.firstChild) ghostGroup.removeChild(ghostGroup.firstChild);
        if (!state.showGhost) return;

        // Test SSA: given (a, b, A) — side a opposite angle A, with side b
        const g = currentGeometry(state);
        const result = solveTriangle({ a: g.a, b: g.b, A: g.A });

        if (!('ambiguous' in result)) return;

        // We have two valid triangles. The primary is already rendered.
        // For the ghost we reconstruct its vertices from the second solution.
        const [, t2] = result.ambiguous;

        // Anchor v1 and v2 (the base c = v1-v2), reconstruct v3 for t2.
        // The base side c in our labelling is |v1-v2|. In t2 we get a different c'.
        // We place v3' such that:
        //   |v3'-v2| = t2.a  and  angle at v1 = t2.A
        const { v1, v2 } = state;
        const baseVec = { x: v2.x - v1.x, y: v2.y - v1.y };
        // Angle of v1→v2
        const baseAngle = Math.atan2(baseVec.y, baseVec.x);
        // Ghost v3 is at angle (baseAngle + t2.A) from v1 at distance t2.b
        const ghostAngle = baseAngle + t2.A;
        const ghostV3: Point = {
          x: v1.x + t2.b * Math.cos(ghostAngle),
          y: v1.y + t2.b * Math.sin(ghostAngle),
        };

        // Render ghost as a dashed/muted triangle
        createTriangle(ghostGroup, {
          vertices: [v1, v2, ghostV3],
          className: 'tg-ghost',
          i18n: ctx.i18n,
        });
      }

      // ---------------------------------------------------------------------------
      // update — reconcile view from state
      // ---------------------------------------------------------------------------

      function update(state: State): void {
        // First: refresh the live-state reference used by drag callbacks.
        current = state;

        const { v1, v2, v3 } = state;

        triangle.update({ vertices: [v1, v2, v3] });
        handles[0]!.setPosition(v1.x, v1.y);
        handles[1]!.setPosition(v2.x, v2.y);
        handles[2]!.setPosition(v3.x, v3.y);
        redrawReadouts(state);
        maybeShowGhost(state);
      }

      return {
        update,
        dispose() {
          for (const h of handles) h.destroy();
          triangle.destroy();
          axes.destroy();
          actionBar.destroy();
          viewport.dispose();
          for (const off of i18nUnsubs) off();
          i18nUnsubs.length = 0;
          svg.remove();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Alias for the existing index.ts + test imports (behaviorally identical).
export const mountTriangleSolver = experiment.mount.bind(experiment);
